import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import { Icon } from '../../../../atoms/Icon'
import type { EmploymentTrend } from '../../employmentService'

interface PublicPrivateDistributionChartProps {
  data: EmploymentTrend[]
  height?: number
  type?: 'employment' | 'establishments'
  t: TFunction
}

export interface PublicPrivateDistributionChartRef {
  exportChart: () => string | null
}

export const PublicPrivateDistributionChart = forwardRef<
  PublicPrivateDistributionChartRef,
  PublicPrivateDistributionChartProps
>(({ data, height = 400, type = 'employment', t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Sort data by year
    const sortedData = [...data].sort((a, b) => a.year - b.year)

    // Prepare data arrays based on type
    const years = sortedData.map((d) => d.year.toString())
    const publicData =
      type === 'employment'
        ? sortedData.map((d) => d.total_employment_public_sector)
        : sortedData.map((d) => d.total_establishments_public_sector)
    const privateData =
      type === 'employment'
        ? sortedData.map((d) => d.total_employment_private_sector)
        : sortedData.map((d) => d.total_establishments_private_sector)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: function (params: any) {
          const year = params[0].axisValue
          const publicLabel = t('charts.publicPrivate.legend.publicSector')
          const privateLabel = t('charts.publicPrivate.legend.privateSector')
          const publicVal =
            params.find((p: any) => p.seriesName === publicLabel)?.value || 0
          const privateVal =
            params.find((p: any) => p.seriesName === privateLabel)?.value || 0
          const total = publicVal + privateVal

          const publicPct =
            total > 0 ? ((publicVal / total) * 100).toFixed(1) : '0'
          const privatePct =
            total > 0 ? ((privateVal / total) * 100).toFixed(1) : '0'

          return `
            <div style="text-align: left;">
              <strong>${t('charts.publicPrivate.tooltips.year')}: ${year}</strong><br/>
              <span style="color: #3b82f6;">● ${t('charts.publicPrivate.tooltips.public')}: ${publicVal.toLocaleString()} (${publicPct}%)</span><br/>
              <span style="color: #10b981;">● ${t('charts.publicPrivate.tooltips.private')}: ${privateVal.toLocaleString()} (${privatePct}%)</span><br/>
              <span style="color: #6b7280;">● ${t('charts.publicPrivate.tooltips.total')}: ${total.toLocaleString()}</span>
            </div>
          `
        },
      },
      legend: {
        data: [
          t('charts.publicPrivate.legend.publicSector'),
          t('charts.publicPrivate.legend.privateSector'),
        ],
        top: 10,
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: {
          fontSize: 11,
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
      },
      yAxis: {
        type: 'value',
        name:
          type === 'employment'
            ? t('charts.publicPrivate.axis.employment')
            : t('charts.publicPrivate.axis.establishments'),
        axisLabel: {
          fontSize: 11,
          formatter: function (value: number) {
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'k'
            }
            return value.toString()
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#f3f4f6',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: t('charts.publicPrivate.legend.publicSector'),
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: '#3b82f6',
          },
          emphasis: {
            focus: 'series',
          },
          data: publicData,
        },
        {
          name: t('charts.publicPrivate.legend.privateSector'),
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: '#10b981',
          },
          emphasis: {
            focus: 'series',
          },
          data: privateData,
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height, type, t])

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.publicPrivate.noData')}
      </div>
    )
  }

  const sortedData = [...data].sort((a, b) => a.year - b.year)
  const lastYear = sortedData[sortedData.length - 1]

  const publicVal =
    type === 'employment'
      ? lastYear.total_employment_public_sector
      : lastYear.total_establishments_public_sector
  const privateVal =
    type === 'employment'
      ? lastYear.total_employment_private_sector
      : lastYear.total_establishments_private_sector

  const total = publicVal + privateVal
  const publicPct = total > 0 ? (publicVal / total) * 100 : 0
  const privatePct = total > 0 ? (privateVal / total) * 100 : 0

  return (
    <div>
      <div
        ref={chartRef}
        style={{
          width: '100%',
          height: `${height}px`,
          minHeight: '300px',
        }}
      />

      {/* Distribution insights */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icon name="landmark" size="xs" />{' '}
          {t('charts.publicPrivate.summary.title', { year: lastYear.year })}
        </div>
        <div
          style={{
            fontSize: '13px',
            color: '#4b5563',
            lineHeight: '1.4',
          }}
        >
          • <strong>{t('charts.publicPrivate.summary.publicSector')}:</strong>{' '}
          {publicVal.toLocaleString()} ({publicPct.toFixed(1)}%)
          <br />•{' '}
          <strong>
            {t('charts.publicPrivate.summary.privateSector')}:
          </strong>{' '}
          {privateVal.toLocaleString()} ({privatePct.toFixed(1)}%)
          <br />•{' '}
          <strong>
            {t('charts.publicPrivate.summary.marketCharacter')}:
          </strong>{' '}
          {publicPct > 40
            ? t('charts.publicPrivate.summary.governmentDominated')
            : publicPct > 25
              ? t('charts.publicPrivate.summary.balancedMix')
              : t('charts.publicPrivate.summary.privateDriven')}
          <br />•{' '}
          <strong>
            {t('charts.publicPrivate.summary.investmentImplications')}:
          </strong>{' '}
          {publicPct > 40
            ? t('charts.publicPrivate.summary.stableTenantBase')
            : t('charts.publicPrivate.summary.marketDrivenDemand')}
        </div>
      </div>
    </div>
  )
})

PublicPrivateDistributionChart.displayName = 'PublicPrivateDistributionChart'
